import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WEBSOCKET } from '../environment';
import { setDchatStream } from 'slices/dchatSlice';
import { useDispatch, useStore } from 'react-redux';
import type { Store } from 'redux';
interface WebsocketContextType {
  needsRefresh: boolean;
  isConnected: boolean;
}

const WebsocketContext = createContext<WebsocketContextType | undefined>(
  undefined
);

const validEndpoints = {
  '/dice/editgroup': ['groups'],
  '/dice/deleteGroup': ['groups'],
  '/dice/inviteTogroup': ['groups', 'users'],
  '/dice/removeUserFromGroup': ['groups', 'users'],
  'dice/roles/createRoleWithPermissions': ['groups', 'roles'],
  'dice/roles/unAssignUserFromRole': ['groups', 'roles', 'users'],
  '/dice/roles/assignUserToRole': ['groups', 'roles', 'users'],
  '/dice/roles/editRoleAndPermissions': ['groups', 'roles'],
};

// React Query base keys to invalidate when the DGPT agent writes an ICS entity,
// keyed first by the ICS entity name (generic create/update_ics_record tools)
// then by the originating tool name (writes that aren't entity-scoped). Keys
// match the `base` of the corresponding IAP hooks (e.g. incidentObjectiveHooks).
const icsUpdateQueryKeys: Record<string, string[]> = {
  // by entity
  objective: ['incident_objectives'],
  action: ['incident_actions'],
  resource: ['resource_sets', 'resources-in-incident'],
  // by tool (no entity field)
  update_incident_info: [
    'incident_info',
    'incidentInfo',
    'incidentStatusSummary',
  ],
};

// React Query base keys to invalidate when the DGPT agent writes an ORG-level
// record (OrgWriteTool). Keyed by entity then tool name, like icsUpdateQueryKeys.
// Bases match the `all` key of the corresponding hooks (orgEventAutomationKeys).
const orgUpdateQueryKeys: Record<string, string[]> = {
  org_event_automation_rule: ['org-event-automation-rules'],
  draft_org_event_automation_rule: ['org-event-automation-rules'],
};
const orgUpdateFallbackKeys = ['org-event-automation-rules'];

// Fallback when the agent reports a write we don't have a precise mapping for:
// refresh the entities most commonly edited so the UI never stays stale.
const icsUpdateFallbackKeys = [
  'incident_objectives',
  'incident_actions',
  'incident_info',
  'incidentStatusSummary',
];

export const WebsocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const store: Store = useStore();

  useEffect(() => {
    const comsWebSocket = WEBSOCKET;
    let client: WebSocket | null = null;
    let retries = 0;
    const maxRetries = 4;
    const retryDelay = 3000;

    const connectWebSocket = () => {
      try {
        client = new WebSocket(comsWebSocket);
      } catch (error) {
        console.error('Error creating WebSocket client', error);
        return;
      }

      client.onopen = () => {
        // Set connection state in React Query cache
        setIsConnected(true);
        retries = 0;
      };

      client.onmessage = (message) => {
        if (message && message.data) {
          const payload = JSON.parse(message.data);
          if (payload.endpoint) {
            const currStore = store.getState();
            const userId = currStore.app.user.user_guid;
            const groups = currStore.app.groups;
            if (Array.isArray(groups) && payload.group) {
              const implicatedGroup = groups.find(
                (group: Group) => group.group_guid === payload.group
              );

              if (
                implicatedGroup &&
                userId &&
                userId !== payload.requesting_user
              ) {
                invalidateRelatedQueries(payload.endpoint);
                setNeedsRefresh(true);
              }
            }
          }
          if (payload.dchat_stream) {
            // DGPT token stream frame. Render only when it belongs to this
            // user AND the chat currently open — frames are broadcast to all
            // connected clients, so the filter is what scopes them.
            const { dchat_id, user_guid, streamtext } = payload.dchat_stream;
            const currStore = store.getState();
            const currentUserId = currStore.app.user.user_guid;
            const openDchatId = currStore.dchat.dchat?.id;
            if (
              streamtext &&
              user_guid === currentUserId &&
              dchat_id === openDchatId
            ) {
              dispatch(setDchatStream(streamtext));
            }
          }
          if (payload.dchat_ics_update) {
            // The agent wrote an ICS entity (objective, action, situation
            // summary, etc.). Refetch the affected data so the open IAP/ICS
            // page reflects it immediately. Scoped to the incident the viewer
            // is on — match the ROUTED incident (source of truth for the mounted
            // workspace), Redux currentIncident, OR the open chat's incident.
            // The chat-incident check is the robust one for agent-created
            // incidents: the agent builds for the chat's incident and the user is
            // (being) navigated there, so it holds even if the route/redux lag the
            // write. invalidateQueries only refetches MOUNTED queries, so matching
            // a not-currently-viewed incident is harmless.
            const { incident_id, entity, tool } = payload.dchat_ics_update;
            const currStore = store.getState();
            const openIncidentId = currStore.app.currentIncident?.id;
            const chatIncidentId = currStore.dchat?.dchat?.incident_id;
            const routeIncidentId =
              typeof window !== 'undefined'
                ? window.location.pathname.match(/\/iap\/([0-9a-fA-F-]+)/)?.[1]
                : undefined;
            if (
              incident_id &&
              (incident_id === routeIncidentId ||
                incident_id === openIncidentId ||
                incident_id === chatIncidentId)
            ) {
              const queryBases: string[] =
                icsUpdateQueryKeys[entity] ||
                icsUpdateQueryKeys[tool] ||
                icsUpdateFallbackKeys;
              queryBases.forEach((base: string) => {
                queryClient.invalidateQueries({ queryKey: [base] });
              });
            }
          }
          if (payload.dchat_org_update) {
            // The agent wrote an org-level record (e.g. drafted an Event
            // Settings automation rule). Refetch for viewers of that org so the
            // settings list shows the draft without a reload.
            const { group_guid, entity, tool } = payload.dchat_org_update;
            const currStore = store.getState();
            const currentGroupGuid =
              currStore.app.currentlySelectedGroup?.group_guid;
            if (group_guid && group_guid === currentGroupGuid) {
              const queryBases: string[] =
                orgUpdateQueryKeys[entity] ||
                orgUpdateQueryKeys[tool] ||
                orgUpdateFallbackKeys;
              queryBases.forEach((base: string) => {
                queryClient.invalidateQueries({ queryKey: [base] });
              });
            }
          }
        }
      };

      client.onerror = () => {
        console.error('WebSocket encountered an error.');
        setIsConnected(false);
      };

      client.onclose = () => {
        setIsConnected(false);

        if (retries < maxRetries) {
          setTimeout(() => {
            retries += 1;
            console.warn(
              `WebSocket connection lost. Retrying... (${retries}/${maxRetries})`
            );
            connectWebSocket();
          }, retryDelay);
        } else {
          console.error('Failed to reconnect after 4 attempts.');
        }
      };
    };

    connectWebSocket();

    const stayAliveInterval = setInterval(() => {
      if (client && client.readyState === WebSocket.OPEN) {
        client.send('stay alive');
      }
    }, 40000);

    return () => {
      clearInterval(stayAliveInterval);
      if (client) {
        client.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidateRelatedQueries = (endpoint: string) => {
    const queriesToInvalidate =
      validEndpoints[endpoint as keyof typeof validEndpoints];
    if (queriesToInvalidate) {
      // Invalidate each query type associated with this endpoint
      queriesToInvalidate.forEach((queryType) => {
        queryClient.invalidateQueries({ queryKey: [queryType] });
      });
    }
  };

  return (
    <WebsocketContext.Provider
      value={{
        needsRefresh: needsRefresh,
        isConnected: isConnected,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};

export const useWebsocket = () => {
  const context = useContext(WebsocketContext);
  if (!context) {
    throw new Error('useWebsocket must be used within WebsocketProvider');
  }
  return context;
};
